import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Loader2, AlertTriangle } from 'lucide-react';

/**
 * AddressAutocomplete — input con autocomplete indirizzi, senza API key e
 * senza costi, con DUE provider indipendenti per affidabilità:
 *
 * 1. Nominatim (OpenStreetMap Foundation) — provider PRINCIPALE. È il
 *    servizio ufficiale del progetto OpenStreetMap, coerente con quanto già
 *    whitelistato nella Content-Security-Policy dell'hosting.
 * 2. Photon (Komoot) — riserva automatica: se Nominatim non risponde (rete,
 *    timeout, errore) o restituisce un errore HTTP, si tenta subito Photon
 *    prima di arrendersi. Due provider indipendenti abbassano di molto la
 *    probabilità che l'autocomplete sia del tutto fuori uso in un dato
 *    momento (sono entrambi demo pubbliche gratuite, senza SLA garantito).
 *
 * IMPORTANTE — limiti onesti di un servizio gratuito senza API key:
 * sia Nominatim che Photon sono infrastrutture di comunità, gratuite e
 * "best effort": nessuna delle due garantisce disponibilità al 100%, e
 * entrambe possono limitare/bloccare temporaneamente il traffico in caso di
 * uso intensivo (policy di fair-use). Per un'app con traffico reale e
 * importante, se l'affidabilità diventasse critica, l'unica soluzione
 * davvero "a prova di bomba" è un'istanza self-hosted di Photon/Nominatim
 * oppure un provider commerciale con SLA (es. Geoapify o LocationIQ hanno
 * piani gratuiti più ampi con API key, Google Places è a pagamento oltre la
 * soglia gratuita). Questo componente resta comunque gratuito al 100% e
 * copre bene l'uso normale di un'applicazione con traffico moderato.
 *
 * Se ENTRAMBI i provider falliscono, il componente lo dice chiaramente
 * invece di sparire in silenzio (prima si limitava a un console.warn,
 * invisibile per chi non apre gli strumenti sviluppatore).
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const PHOTON_URL = 'https://photon.komoot.io/api/';

// Bounding box Italia
const ITALY_BBOX_PHOTON = '6.62,36.62,18.52,47.09'; // lon_min,lat_min,lon_max,lat_max
const ITALY_VIEWBOX_NOMINATIM = '6.62,47.09,18.52,36.62'; // lon_min,lat_max,lon_max,lat_min

/* ─────────────────────────── Nominatim ─────────────────────────── */

async function fetchNominatim(query, signal) {
  const params = new URLSearchParams({
    format: 'jsonv2',
    q: query,
    countrycodes: 'it',
    addressdetails: '1',
    limit: '6',
    'accept-language': 'it',
    viewbox: ITALY_VIEWBOX_NOMINATIM,
    bounded: '1',
  });
  const res = await fetch(`${NOMINATIM_URL}?${params}`, {
    signal,
    headers: { 'Accept': 'application/json' },
  });
  if (!res.ok) throw new Error(`Nominatim HTTP ${res.status}`);
  const data = await res.json();
  return (Array.isArray(data) ? data : []).map(r => ({ provider: 'nominatim', raw: r }));
}

function extractPlaceNominatim(raw) {
  const a = raw.address || {};
  const street = a.road || a.pedestrian || a.footway || '';
  const civico = a.house_number || '';
  const indirizzo = street ? [street, civico].filter(Boolean).join(', ') : (raw.display_name || '').split(',')[0];
  const citta = a.city || a.town || a.village || a.municipality || a.county || '';
  const cap = a.postcode || '';
  return {
    indirizzo,
    citta,
    cap,
    // Il civico è arrivato davvero dall'API (non va confuso con un'eventuale
    // cifra nel NOME della via, es. "Via 4 Novembre", "Via 20 Settembre",
    // "Via 25 Aprile": queste vie hanno un numero nel nome ma NON hanno un
    // civico restituito da Nominatim/Photon — vedi withTypedCivico più sotto).
    hasCivico: !!civico,
    lat: raw.lat ? parseFloat(raw.lat) : null,
    lng: raw.lon ? parseFloat(raw.lon) : null,
  };
}

function mainTextNominatim(raw) {
  const a = raw.address || {};
  const street = a.road || a.pedestrian || a.footway || '';
  if (street) return [street, a.house_number].filter(Boolean).join(' ');
  return (raw.display_name || '').split(',')[0];
}

function secondaryTextNominatim(raw) {
  const a = raw.address || {};
  const city = a.city || a.town || a.village || a.municipality || '';
  const state = a.county || a.state || '';
  return [a.postcode, city, state].filter(Boolean).join(', ');
}

/* ───────────────────────────── Photon (riserva) ───────────────────────────── */

async function fetchPhoton(query, signal) {
  const params = new URLSearchParams({
    q: query,
    limit: '6',
    lang: 'it',
    bbox: ITALY_BBOX_PHOTON,
  });
  const res = await fetch(`${PHOTON_URL}?${params}`, {
    signal,
    headers: { 'Accept': 'application/json' },
  });
  if (!res.ok) throw new Error(`Photon HTTP ${res.status}`);
  const data = await res.json();
  const features = (data.features || []).filter(f => {
    const cc = (f.properties?.country_code || '').toLowerCase();
    return cc === 'it' || cc === '';
  });
  return features.map(f => ({ provider: 'photon', raw: f }));
}

function extractPlacePhoton(feature) {
  const props = feature.properties || {};
  const [lon, lat] = feature.geometry?.coordinates || [null, null];
  const street = props.street || '';
  const civico = props.housenumber || '';
  const indirizzo = street ? [street, civico].filter(Boolean).join(', ') : (props.name || '');
  const citta = props.city || props.town || props.village || props.municipality || '';
  const cap = props.postcode || '';
  return {
    indirizzo, citta, cap,
    hasCivico: !!civico,
    lat: typeof lat === 'number' ? lat : null,
    lng: typeof lon === 'number' ? lon : null,
  };
}

function mainTextPhoton(props) {
  if (props.street) return [props.street, props.housenumber].filter(Boolean).join(' ');
  return props.name || props.city || '';
}

function secondaryTextPhoton(props) {
  const city = props.city || props.town || props.village || props.municipality || '';
  const state = props.county || props.state || '';
  return [props.postcode, city, state].filter(Boolean).join(', ');
}

/* ─────────────────── Wrapper unico: normalizza entrambi i provider ─────────────────── */

function getMainText(item) {
  return item.provider === 'nominatim' ? mainTextNominatim(item.raw) : mainTextPhoton(item.raw.properties || {});
}
function getSecondaryText(item) {
  return item.provider === 'nominatim' ? secondaryTextNominatim(item.raw) : secondaryTextPhoton(item.raw.properties || {});
}
function extractPlace(item) {
  return item.provider === 'nominatim' ? extractPlaceNominatim(item.raw) : extractPlacePhoton(item.raw);
}
// Vero solo se l'API ha restituito un civico reale per questo suggerimento
// (campo house_number/housenumber), non se il NOME della via contiene una
// cifra (es. "Via 4 Novembre") — vedi commento in extractPlaceNominatim.
function itemHasApiCivico(item) {
  if (item.provider === 'nominatim') return !!(item.raw.address && item.raw.address.house_number);
  return !!(item.raw.properties && item.raw.properties.housenumber);
}
function getKey(item, i) {
  return item.provider === 'nominatim' ? `nom-${item.raw.place_id}-${i}` : `pho-${item.raw.properties?.osm_id}-${i}`;
}

/*
 * Nominatim/Photon (provider gratuiti OSM) hanno una copertura del civico
 * molto disomogenea in Italia: per molti indirizzi il numero civico non è
 * indicizzato come punto singolo, quindi il suggerimento restituito è spesso
 * solo la via, anche se l'utente lo aveva digitato correttamente nella
 * ricerca. Per non perderlo, se il suggerimento non contiene un civico lo
 * recuperiamo dal testo digitato dall'utente (es. "Via Roma 15" o
 * "Via Roma 15/A" → "15", "15/A") — sia per mostrarlo già nella tendina dei
 * suggerimenti, sia per completare l'indirizzo alla selezione.
 *
 * Il civico si cerca PRIMA nel segmento di testo che precede un'eventuale
 * virgola (es. "Via Roma 15, Milano" → si cerca in "Via Roma 15", non nella
 * stringa intera, altrimenti "Milano" alla fine farebbe fallire il match).
 * Se non c'è virgola, si cerca in fondo all'intera stringa digitata.
 */
function extractTypedHouseNumber(text) {
  if (!text) return '';
  const trimmed = text.trim();
  const firstSegment = (trimmed.split(',')[0] || '').trim();
  const civicoPattern = /(\d{1,4}\s?[/-]?\s?[a-zA-Z]{0,2})\s*$/;
  const inFirstSegment = firstSegment.match(civicoPattern);
  if (inFirstSegment) return inFirstSegment[1].replace(/\s+/g, '');
  const inWholeString = trimmed.match(civicoPattern);
  return inWholeString ? inWholeString[1].replace(/\s+/g, '') : '';
}

/**
 * Se il suggerimento non ha un civico REALE restituito dall'API (vedi
 * itemHasApiCivico), prova ad aggiungere il civico digitato dall'utente —
 * vedi extractTypedHouseNumber sopra. Prima si controllava "il testo del
 * suggerimento contiene una cifra?", ma questo falliva silenziosamente per
 * moltissime vie italiane che hanno un numero nel NOME (Via 4 Novembre, Via
 * 20 Settembre, Via 25 Aprile, Via 2 Giugno...): la cifra del nome veniva
 * scambiata per un civico già presente e il civico digitato veniva perso.
 */
function withTypedCivico(mainText, typedValue, hasApiCivico) {
  if (!mainText || hasApiCivico) return mainText;
  const civico = extractTypedHouseNumber(typedValue);
  return civico ? `${mainText} ${civico}` : mainText;
}

const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  placeholder,
  className,
  showHint = true,
}) => {
  const containerRef  = useRef(null);
  const debounceRef   = useRef(null);
  const abortRef      = useRef(null);

  const [suggestions, setSuggestions]   = useState([]);
  const [open, setOpen]                 = useState(false);
  const [highlighted, setHighlighted]   = useState(-1);
  const [fetching, setFetching]         = useState(false);
  const [errored, setErrored]           = useState(false); // entrambi i provider hanno fallito

  /* Chiude il menu se si clicca fuori dal componente */
  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      setFetching(false);
      return;
    }
    const q = query.trim();

    // Annulla la chiamata precedente se ancora in corso
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setFetching(true);
    setErrored(false);
    try {
      let items = [];
      try {
        items = await fetchNominatim(q, controller.signal);
      } catch (nominatimErr) {
        if (nominatimErr.name === 'AbortError') throw nominatimErr;
        // Nominatim non raggiungibile/errore → riserva automatica su Photon
        console.warn('AddressAutocomplete: Nominatim non disponibile, provo Photon:', nominatimErr.message);
        items = await fetchPhoton(q, controller.signal);
      }

      setSuggestions(items);
      setOpen(items.length > 0);
      setHighlighted(-1);
      setErrored(false);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.warn('AddressAutocomplete: entrambi i provider (Nominatim e Photon) non hanno risposto:', err);
        setSuggestions([]);
        setOpen(false);
        setErrored(true);
      }
    } finally {
      setFetching(false);
    }
  }, []);

  const handleInputChange = (val) => {
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      setErrored(false);
      return;
    }
    setFetching(true);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
  };

  const handleSelect = (item) => {
    const place = extractPlace(item);
    // Se il suggerimento non ha il civico ma l'utente lo aveva digitato,
    // lo recuperiamo dal testo invece di perderlo silenziosamente (vedi
    // commento su extractTypedHouseNumber).
    place.indirizzo = withTypedCivico(place.indirizzo, value, place.hasCivico);
    // Riempi il campo di testo con indirizzo completo leggibile
    const display = [place.indirizzo, place.citta].filter(Boolean).join(', ');
    onChange(display || value);
    onPlaceSelected(place);
    setSuggestions([]);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlighted >= 0) {
        e.preventDefault();
        handleSelect(suggestions[highlighted]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 pointer-events-none z-10" />
        <input
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className={className || 'w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'}
        />
        {fetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 animate-spin" />
        )}

        {/* Menu a tendina con i suggerimenti */}
        {open && suggestions.length > 0 && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {suggestions.map((item, i) => {
              const mainText = withTypedCivico(getMainText(item), value, itemHasApiCivico(item));
              const secText  = getSecondaryText(item);
              return (
                <button
                  key={getKey(item, i)}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHighlighted(i)}
                  className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 border-b border-gray-50 last:border-b-0 transition ${
                    highlighted === i ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <MapPin className="w-3.5 h-3.5 text-gray-300 shrink-0 mt-0.5" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">
                      {mainText}
                    </span>
                    {secText && (
                      <span className="block text-xs text-gray-400 truncate">
                        {secText}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
            <div className="px-3 py-1.5 border-t border-gray-100 flex items-center gap-1">
              <span className="text-[10px] text-gray-300">Dati indirizzi</span>
              <a
                href="https://www.openstreetmap.org/copyright"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-gray-400 hover:underline"
                onMouseDown={(e) => e.stopPropagation()}
              >
                © OpenStreetMap
              </a>
            </div>
          </div>
        )}
      </div>

      {errored && (
        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          Ricerca indirizzi momentaneamente non disponibile. Puoi comunque scrivere l'indirizzo a mano.
        </p>
      )}
      {showHint && !open && !errored && (
        <p className="text-xs text-gray-400 mt-1">
          Scrivi almeno 3 caratteri e scegli un indirizzo dal menu.
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
